import React, { useState, useEffect } from 'react';
import { Button, Tooltip } from '@mui/material';
import { GetApp, PhoneAndroid } from '@mui/icons-material';
import PWAInstallPrompt from './PWAInstallPrompt';

interface PWAInstallButtonProps {
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showManualPrompt, setShowManualPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Détecter si c'est iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Détecter si l'app est déjà installée (mode standalone)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone ||
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Écouter l'événement beforeinstallprompt (Chrome/Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Écouter l'événement d'installation réussie
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // Si on a un prompt natif (Chrome/Android)
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          console.log('PWA installée avec succès');
          setIsInstalled(true);
        }

        setDeferredPrompt(null);
      } catch (error) {
        console.error('Erreur lors de l\'installation:', error);
        setShowManualPrompt(true);
      }
    } else {
      // Sinon, afficher les instructions manuelles
      setShowManualPrompt(true);
    }
  };

  // Ne pas afficher le bouton si l'app est déjà installée
  if (isStandalone || isInstalled) {
    return null;
  }

  const getButtonText = () => {
    if (isIOS) {
      return 'Installer sur iPhone';
    }
    return 'Installer l\'application';
  };

  const getTooltipText = () => {
    if (isIOS) {
      return 'Installez l\'application sur votre iPhone pour recevoir des notifications';
    }
    if (deferredPrompt) {
      return 'Cliquez pour installer l\'application en un clic';
    }
    return 'Voir les instructions d\'installation';
  };

  return (
    <>
      <Tooltip title={getTooltipText()} arrow>
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          color="primary"
          startIcon={isIOS ? <PhoneAndroid /> : <GetApp />}
          onClick={handleInstallClick}
          sx={{
            fontWeight: 600,
            ...(variant === 'outlined' && {
              borderColor: 'white',
              color: 'white',
              borderWidth: 2,
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                borderWidth: 2,
              },
            }),
            ...(variant === 'contained' && {
              bgcolor: 'white',
              color: 'primary.main',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'grey.100',
                boxShadow: 6,
                transform: 'translateY(-2px)',
              },
            }),
          }}
        >
          {getButtonText()}
        </Button>
      </Tooltip>

      <PWAInstallPrompt
        open={showManualPrompt}
        onClose={() => setShowManualPrompt(false)}
      />
    </>
  );
};

export default PWAInstallButton;
